import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  SaveConfirmationAction,
  SaveConfirmationDialogData,
  SaveConfirmationDialogResult,
} from '@portal-workspace/grow-shared-library';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  templateUrl: './save-confirmation.dialog.html',
  styleUrls: ['./save-confirmation.dialog.scss'],
  standalone: true,
  imports: [MatButtonModule, MatDialogModule, MatCheckboxModule],
})
export class SaveConfirmationDialog {

  // Ids of skippable actions the officer has unticked; everything else proceeds as listed.
  readonly skippedActionIds = new Set<string>();

  constructor(@Inject(MAT_DIALOG_DATA) public data: SaveConfirmationDialogData,
              private dialogRef: MatDialogRef<SaveConfirmationDialog, SaveConfirmationDialogResult>) {
  }

  get introMessage(): string {
    if (this.data.message) {
      return this.data.message;
    }
    if (!this.data.consequences.length) {
      return 'Are you sure you want to save these changes?';
    }
    const kinds = new Set(
      this.data.consequences.flatMap(item => item.actions).map(action => action.kind).filter(Boolean),
    );
    const hasConsentIssue = kinds.has('consentIssue');
    const hasConsentReissue = kinds.has('consentReissue');
    const hasConsent = hasConsentIssue || hasConsentReissue;
    const hasReverify = kinds.has('reverify');
    if (hasConsent && hasReverify) {
      return 'Saving will issue or re-issue privacy consent forms and/or re-verify Digital ID for each item ticked below. Unticked items will be skipped.';
    }
    if (hasConsentIssue && hasConsentReissue) {
      return 'Saving will issue or re-issue privacy consent forms for each item ticked below. Unticked items will be skipped.';
    }
    if (hasConsentIssue) {
      return 'Saving will issue a privacy consent form, where required, to each individual ticked below.';
    }
    if (hasConsentReissue) {
      return 'Saving will re-issue a privacy consent form for each individual ticked below. Their current consent will be invalidated, and a new form sent for signing.';
    }
    if (hasReverify) {
      return 'Saving will re-verify Digital ID for each individual ticked below.';
    }
    return 'Saving these changes will:';
  }

  skipHintFor(action: SaveConfirmationAction): string {
    if (action.kind === 'consentIssue' || action.kind === 'consentReissue') {
      return 'A new consent form will not be issued. This decision will be logged for audit purposes.';
    }
    if (action.kind === 'reverify') {
      return 'Digital ID will not be re-verified. This decision will be logged for audit purposes.';
    }
    if (action.kind === 'creditFlowRun') {
      return 'The credit flow will not be run. The guarantor change is still saved. This decision will be logged for audit purposes.';
    }
    return 'This step will be skipped. This decision will be logged for audit purposes.';
  }

  onToggle(actionId: string, change: MatCheckboxChange) {
    if (change.checked) {
      this.skippedActionIds.delete(actionId);
    } else {
      this.skippedActionIds.add(actionId);
    }
  }

  isSkipped(actionId: string): boolean {
    return this.skippedActionIds.has(actionId);
  }

  onSubmit(readyForSubmission: boolean) {
    const result: SaveConfirmationDialogResult = {
      readyForSubmission,
      ...(readyForSubmission && this.skippedActionIds.size ? { skippedActionIds: [...this.skippedActionIds] } : {}),
    };
    this.dialogRef.close(result);
  }
}
